// src/components/auth/Login.jsx
// ─────────────────────────────────────────────────────────────────────────────
//  TelcoFair · Login (Admin Portal)
//  "TelcoFair Glass Workspace" — full liquid glass theme with animations
//  Only Super Admin users can access (NOT company_admin)
// ─────────────────────────────────────────────────────────────────────────────

import React, { useState, useRef, useMemo } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import {
    Box,
    Paper,
    Typography,
    TextField,
    Button,
    Container,
    Alert,
    FormControlLabel,
    Checkbox,
    InputAdornment,
    IconButton,
    Divider,
    CircularProgress,
    Chip,
} from '@mui/material';
import {
    Email as EmailIcon,
    Visibility,
    VisibilityOff,
    Security as SecurityIcon,
    Business as BusinessIcon,
    People as PeopleIcon,
    TrendingUp as TrendingUpIcon,
    Verified as VerifiedIcon,
    Shield as ShieldIcon,
    Lock as LockIcon,
    CheckCircle as CheckCircleIcon,
    FiberManualRecord as LiveIcon,
    AdminPanelSettings as AdminIcon,
    Fingerprint as FingerprintIcon,
    ArrowForward as ArrowForwardIcon,
    Diamond as DiamondIcon,
    Storefront as StorefrontIcon,
    AutoAwesome as AutoAwesomeIcon,
    Stars as StarsIcon,
    Handshake as HandshakeIcon,
    Analytics as AnalyticsIcon,
    Speed as SpeedIcon,
} from '@mui/icons-material';
import { motion, AnimatePresence } from 'framer-motion';
import toast from 'react-hot-toast';
import { useAuth } from '../../contexts/AuthContext';
import PlatformLogo from '../common/PlatformLogo';
import { friendlyError } from '../../utils/userMessages';

/* ═══════════════════════════════════════════════════════════════════════════
   1 · DESIGN TOKENS
   ═══════════════════════════════════════════════════════════════════════════ */

const palette = {
    canvas: '#F1F6F9',
    canvasDeep: '#E7EFF4',
    paper: '#FFFFFF',
    ink: '#182235',
    inkSoft: '#445168',
    inkLight: '#6D7C91',
    inkFaint: '#92A0AF',
    hairline: 'rgba(31, 62, 82, 0.09)',
    hairlineSoft: 'rgba(31, 62, 82, 0.05)',

    rust: 'var(--primary, #2E6F8E)',
    rustDeep: 'var(--secondary, #42B89B)',
    rustGlow: '#5D9DB8',

    amber: '#F39A24',
    amberGlow: '#FFB953',

    pine: '#13A773',
    pineGlow: '#55C99E',

    teal: '#0F9E9A',
    tealGlow: '#54CDC6',

    plum: '#8B4DE8',
    plumGlow: '#B486FF',

    indigo: '#5B58D6',
    indigoGlow: '#918CF2',

    success: '#16A36A',
    warning: '#E29A20',
    danger: '#D94B5A',
};

const ACCENTS = [palette.rust, palette.teal, palette.amber, palette.pine, palette.plum, palette.indigo];

const GRADIENTS = {
    multi: `linear-gradient(135deg, ${palette.rust}, ${palette.amber}, ${palette.pine}, ${palette.plum})`,
};

const fontDisplay = "'Plus Jakarta Sans', 'Inter', sans-serif";
const fontBody = "'Plus Jakarta Sans', 'Inter', 'Segoe UI', sans-serif";
const fontMono = "'JetBrains Mono', 'SFMono-Regular', monospace";

const FONT_IMPORT = `@import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700;800&family=JetBrains+Mono:wght@400;500;600;700&display=swap');`;

/* ═══════════════════════════════════════════════════════════════════════════
   2 · HELPERS
   ═══════════════════════════════════════════════════════════════════════════ */

const rgba = (color, a = 1) => {
    if (typeof color === 'string' && color.startsWith('var(')) return `color-mix(in srgb, ${color} ${Math.max(0, Math.min(100, Number(a) * 100))}%, transparent)`;
    if (typeof color === 'string' && color.startsWith('rgb')) return color;
    return `rgba(22, 18, 16, ${a})`;
};

/* ═══════════════════════════════════════════════════════════════════════════
   3 · LIQUID BACKDROP
   ═══════════════════════════════════════════════════════════════════════════ */

const LiquidBackdrop = React.memo(function LiquidBackdrop() {
    const motes = useMemo(
        () =>
            Array.from({ length: 35 }, (_, i) => ({
                id: i,
                size: 2 + ((i * 3) % 8),
                x: (i * 37) % 100,
                y: (i * 53) % 100,
                duration: 18 + ((i * 4) % 20),
                delay: (i * 1.8) % 14,
                color: ACCENTS[i % ACCENTS.length],
            })),
        []
    );

    return (
        <Box sx={{ position: 'fixed', inset: 0, zIndex: 0, overflow: 'hidden', pointerEvents: 'none' }}>
            <motion.div
                animate={{ x: [0, -50, 35, 0], y: [0, 35, -25, 0], scale: [1, 1.08, 0.95, 1] }}
                transition={{ duration: 28, repeat: Infinity, ease: 'easeInOut' }}
                style={{
                    position: 'absolute', top: '-10%', left: '-3%',
                    width: 550, height: 550, borderRadius: '50%',
                    background: `radial-gradient(circle at 40% 40%, color-mix(in srgb,var(--primary,#2E6F8E) 20%,transparent), color-mix(in srgb,var(--primary,#2E6F8E) 8%,transparent) 48%, transparent 72%)`,
                    filter: 'blur(50px)',
                }}
            />
            <motion.div
                animate={{ x: [0, 55, -30, 0], y: [0, -35, 25, 0], scale: [1, 0.92, 1.08, 1] }}
                transition={{ duration: 33, repeat: Infinity, ease: 'easeInOut' }}
                style={{
                    position: 'absolute', bottom: '-8%', right: '-4%',
                    width: 500, height: 500, borderRadius: '50%',
                    background: `radial-gradient(circle at 55% 45%, rgba(92,33,112,0.18), rgba(15,107,118,0.08) 50%, transparent 72%)`,
                    filter: 'blur(50px)',
                }}
            />
            <motion.div
                animate={{ x: ['-50%', '-54%', '-46%', '-50%'], y: ['-50%', '-46%', '-54%', '-50%'], scale: [1, 1.05, 0.97, 1] }}
                transition={{ duration: 30, repeat: Infinity, ease: 'easeInOut' }}
                style={{
                    position: 'absolute', top: '45%', left: '50%',
                    width: 650, height: 650, borderRadius: '50%',
                    background: `radial-gradient(circle, rgba(176,122,24,0.12), transparent 65%)`,
                    filter: 'blur(70px)',
                }}
            />

            {motes.map((m) => (
                <motion.span
                    key={m.id}
                    initial={{ opacity: 0 }}
                    animate={{
                        y: [`${m.y}%`, `${m.y - 45}%`, `${m.y}%`],
                        x: [`${m.x}%`, `${m.x + 18}%`, `${m.x}%`],
                        opacity: [0, 0.7, 0],
                        scale: [0.2, 1.4, 0.2],
                    }}
                    transition={{ duration: m.duration, delay: m.delay, repeat: Infinity, ease: 'easeInOut' }}
                    style={{
                        position: 'absolute', top: 0, left: 0,
                        width: m.size, height: m.size, borderRadius: '50%',
                        background: `radial-gradient(circle at 30% 30%, rgba(255,255,255,0.9), ${m.color})`,
                        boxShadow: `0 0 25px ${rgba(m.color, 0.5)}`,
                    }}
                />
            ))}

            <Box
                sx={{
                    position: 'absolute', inset: 0, opacity: 0.25,
                    backgroundImage: `linear-gradient(rgba(22,18,16,0.02) 1px, transparent 1px),
                                      linear-gradient(90deg, rgba(22,18,16,0.02) 1px, transparent 1px)`,
                    backgroundSize: '45px 45px',
                    maskImage: 'radial-gradient(circle at 50% 30%, #000 0%, transparent 75%)',
                    WebkitMaskImage: 'radial-gradient(circle at 50% 30%, #000 0%, transparent 75%)',
                }}
            />
        </Box>
    );
});

/* ═══════════════════════════════════════════════════════════════════════════
   4 · GLASS PRIMITIVES
   ═══════════════════════════════════════════════════════════════════════════ */

const GlassIcon = ({ icon, color, size = 42 }) => (
    <Box
        sx={{
            width: size, height: size, flexShrink: 0,
            borderRadius: `${size * 0.34}px`,
            display: 'grid', placeItems: 'center',
            color: '#fff',
            background: `linear-gradient(150deg, ${rgba(color, 0.95)}, ${rgba(color, 0.72)})`,
            boxShadow: `inset 0 1px 0 rgba(255,255,255,0.55), inset 0 -2px 6px rgba(22,18,16,0.18), 0 8px 20px -6px ${rgba(color, 0.55)}`,
            '& svg': { fontSize: size * 0.48, filter: 'drop-shadow(0 1px 1px rgba(0,0,0,.18))' },
        }}
    >
        {icon}
    </Box>
);

const GlassPanel = ({ children, sx = {} }) => {
    const [isHovered, setIsHovered] = useState(false);

    return (
        <motion.div
            initial={{ opacity: 0, y: 35, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1], type: 'spring', stiffness: 90, damping: 22 }}
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
            style={{ height: '100%', width: '100%', minWidth: 0 }}
        >
            <Paper
                elevation={0}
                sx={{
                    position: 'relative',
                    borderRadius: '28px',
                    background: `linear-gradient(155deg, rgba(255,255,255,0.85) 0%, rgba(255,255,255,0.55) 42%, color-mix(in srgb,var(--primary,#2E6F8E) 8%,transparent) 100%)`,
                    backdropFilter: 'blur(24px) saturate(180%)',
                    WebkitBackdropFilter: 'blur(24px) saturate(180%)',
                    border: `1px solid rgba(255,255,255,0.6)`,
                    boxShadow: [
                        `inset 0 1px 0 rgba(255,255,255,0.95)`,
                        `inset 0 -1px 0 rgba(255,255,255,0.35)`,
                        `0 4px 12px rgba(22,18,16,0.04)`,
                        `0 20px 56px -14px color-mix(in srgb,var(--primary,#2E6F8E) 20%,transparent)`,
                        isHovered ? `0 28px 72px -14px color-mix(in srgb,var(--primary,#2E6F8E) 30%,transparent)` : 'none',
                    ].filter(Boolean).join(', '),
                    overflow: 'hidden',
                    transition: 'box-shadow .6s cubic-bezier(.22,1,.36,1), transform .6s cubic-bezier(.22,1,.36,1)',
                    transform: isHovered ? 'translateY(-4px) scale(1.005)' : 'translateY(0) scale(1)',
                    p: { xs: 2.5, md: 3.5 },
                    ...sx,
                }}
            >
                <Box sx={{ position: 'relative', zIndex: 1, display: 'flex', flexDirection: 'column', flex: 1, minWidth: 0 }}>
                    {children}
                </Box>
            </Paper>
        </motion.div>
    );
};

const GlassInput = ({ label, icon, ...props }) => {
    const [focused, setFocused] = useState(false);
    const [hovered, setHovered] = useState(false);

    return (
        <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
        >
            <TextField
                fullWidth
                label={label}
                onFocus={() => setFocused(true)}
                onBlur={() => setFocused(false)}
                onMouseEnter={() => setHovered(true)}
                onMouseLeave={() => setHovered(false)}
                sx={{
                    '& .MuiOutlinedInput-root': {
                        borderRadius: '14px',
                        fontSize: '0.875rem',
                        background: focused ? 'rgba(255,255,255,0.85)' : hovered ? 'rgba(255,255,255,0.65)' : 'rgba(255,255,255,0.45)',
                        backdropFilter: 'blur(12px)',
                        transition: 'all 0.4s cubic-bezier(0.22, 1, 0.36, 1)',
                        '& fieldset': {
                            borderColor: focused ? palette.rust : hovered ? 'color-mix(in srgb,var(--primary,#2E6F8E) 40%,transparent)' : palette.hairline,
                            borderWidth: '1.5px',
                            transition: 'border-color .3s ease',
                        },
                        '&:hover fieldset': { borderColor: 'color-mix(in srgb,var(--primary,#2E6F8E) 50%,transparent)' },
                        '&.Mui-focused fieldset': { borderColor: palette.rust, borderWidth: '2px' },
                        '&.Mui-focused': {
                            boxShadow: `0 0 0 4px color-mix(in srgb,var(--primary,#2E6F8E) 12%,transparent), 0 8px 32px color-mix(in srgb,var(--primary,#2E6F8E) 10%,transparent)`,
                            transform: 'translateY(-2px)',
                            background: 'rgba(255,255,255,0.92)',
                        },
                        '&:hover': {
                            transform: 'translateY(-1px)',
                            background: 'rgba(255,255,255,0.72)',
                        },
                    },
                    '& .MuiInputLabel-root': {
                        color: focused ? palette.rust : hovered ? palette.inkSoft : palette.inkLight,
                        fontFamily: fontBody,
                        fontWeight: 500,
                        transition: 'color .3s ease',
                        '&.Mui-focused': { color: palette.rust },
                    },
                    '& .MuiInputBase-input': {
                        fontFamily: fontBody,
                    },
                }}
                slotProps={{ input: {
                    startAdornment: (
                        <InputAdornment position="start">
                            <motion.div
                                animate={{
                                    scale: focused ? 1.15 : hovered ? 1.05 : 1,
                                    color: focused ? palette.rust : hovered ? palette.inkSoft : palette.inkLight,
                                }}
                                transition={{ duration: 0.3 }}
                            >
                                {icon}
                            </motion.div>
                        </InputAdornment>
                    ),
                    ...props.InputProps,
                } }}
                {...props}
            />
        </motion.div>
    );
};

/* ═══════════════════════════════════════════════════════════════════════════
   5 · MAIN LOGIN COMPONENT
   ═══════════════════════════════════════════════════════════════════════════ */

const Login = () => {
    const navigate = useNavigate();
    const { loginWithEmail, loading } = useAuth();

    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [rememberMe, setRememberMe] = useState(false);
    const [error, setError] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [loginSuccess, setLoginSuccess] = useState(false);
    const [hoveredFeature, setHoveredFeature] = useState(null);

    // Features
    const features = [
        { icon: <AdminIcon  key="config-359-0-0" />, title: 'Admin Access', desc: 'Full system control & oversight', accent: palette.rust },
        { icon: <PeopleIcon  key="config-360-1-0" />, title: 'AI-Powered Matchmaking', desc: 'Smart connections with leaders', accent: palette.pine },
        { icon: <ShieldIcon  key="config-361-2-0" />, title: 'Enterprise Security', desc: 'Military-grade encryption', accent: palette.amber },
        { icon: <AnalyticsIcon  key="config-362-3-0" />, title: 'Business Intelligence', desc: 'Real-time analytics & insights', accent: palette.plum },
        { icon: <HandshakeIcon  key="config-363-4-0" />, title: 'Partner Ecosystem', desc: 'Global telecom network', accent: palette.teal },
        { icon: <SpeedIcon  key="config-364-5-0" />, title: 'Performance Monitoring', desc: 'Real-time system health', accent: palette.indigo },
    ];

    // Security badges
    const securityBadges = [
        { icon: <LockIcon sx={{ fontSize: 13 }}  key="config-369-0-0" />, label: '256-bit SSL', accent: palette.rust },
        { icon: <VerifiedIcon sx={{ fontSize: 13 }}  key="config-370-1-0" />, label: 'MFA Ready', accent: palette.pine },
        { icon: <ShieldIcon sx={{ fontSize: 13 }}  key="config-371-2-0" />, label: 'SOC2 Compliant', accent: palette.amber },
        { icon: <FingerprintIcon sx={{ fontSize: 13 }}  key="config-372-3-0" />, label: 'Biometric Auth', accent: palette.plum },
    ];

    // Super Admin portal login
    const handleLogin = async (e) => {
        e.preventDefault();
        setError('');
        setIsLoading(true);

        try {
            const result = await loginWithEmail(email, password, rememberMe);
            
            // Get role from the result
            const role = result?.userData?.role || result?.user?.role || result?.role;
            
            console.log('🔐 Login result:', result);
            console.log('👤 User role:', role);
            console.log('📧 Email:', email);

            const normalizedRole = String(role || '').toLowerCase().replace(/[\s-]+/g, '_');
            if (!['super_admin', 'superadmin'].includes(normalizedRole)) {
                throw new Error('This account does not have access to the Super Admin Portal.');
            }

            setLoginSuccess(true);
            toast.success('Welcome back, Super Admin!');
            setTimeout(() => navigate('/dashboard'), 1500);
        } catch (err) {
            console.error('❌ Login error:', err);
            setError(friendlyError(err, 'Unable to sign in. Please check your credentials and try again.'));
            setIsLoading(false);
            setLoginSuccess(false);
        }
    };

    /* ── render ───────────────────────────────────────────────────────────── */

    const shell = {
        minHeight: '100vh',
        position: 'relative',
        fontFamily: fontBody,
        color: palette.ink,
        background: `linear-gradient(180deg, ${palette.canvas} 0%, ${palette.canvasDeep} 100%)`,
        display: 'flex',
        alignItems: 'center',
        '&::before': {
            content: '""',
            position: 'fixed',
            inset: 0,
            zIndex: 0,
            pointerEvents: 'none',
            opacity: 0.3,
            backgroundImage:
                'url("data:image/svg+xml,%3Csvg xmlns=%27http://www.w3.org/2000/svg%27 width=%27140%27 height=%27140%27%3E%3Cfilter id=%27n%27%3E%3CfeTurbulence type=%27fractalNoise%27 baseFrequency=%270.85%27 numOctaves=%272%27 stitchTiles=%27stitch%27/%3E%3C/filter%3E%3Crect width=%27100%25%27 height=%27100%25%27 filter=%27url(%23n)%27 opacity=%270.03%27/%3E%3C/svg%3E")',
            animation: 'tfGrain 8s steps(6) infinite',
        },
    };

    return (
        <Box sx={shell}>
            <style>{FONT_IMPORT}</style>
            <LiquidBackdrop />

            {/* Success overlay */}
            <AnimatePresence>
                {loginSuccess && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        style={{
                            position: 'fixed',
                            inset: 0,
                            zIndex: 9999,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            background: 'rgba(241,236,228,0.95)',
                            backdropFilter: 'blur(30px)',
                        }}
                    >
                        <motion.div
                            initial={{ scale: 0.5, rotateY: -90 }}
                            animate={{ scale: 1, rotateY: 0 }}
                            exit={{ scale: 0.5, rotateY: 90 }}
                            transition={{ type: 'spring', stiffness: 200, damping: 15 }}
                            style={{ textAlign: 'center', maxWidth: 420, padding: 40 }}
                        >
                            <motion.div
                                animate={{ scale: [1, 1.12, 1] }}
                                transition={{ duration: 1.2, repeat: Infinity }}
                            >
                                <CheckCircleIcon sx={{ fontSize: 85, color: palette.pine }} />
                            </motion.div>

                            <Typography
                                sx={{
                                    mt: 3,
                                    fontFamily: fontDisplay,
                                    fontWeight: 700,
                                    fontSize: '2.2rem',
                                    color: palette.ink,
                                }}
                            >
                                Welcome, Admin!
                            </Typography>
                            <Typography
                                sx={{
                                    mt: 1,
                                    color: palette.inkSoft,
                                    fontFamily: fontBody,
                                    fontSize: '0.95rem',
                                }}
                            >
                                Redirecting to your dashboard...
                            </Typography>
                            <motion.div
                                initial={{ width: 0 }}
                                animate={{ width: 280 }}
                                transition={{ duration: 1.5, ease: 'easeInOut' }}
                                style={{
                                    height: 3,
                                    background: `linear-gradient(90deg, ${palette.rust}, ${palette.amber}, ${palette.pine})`,
                                    borderRadius: 4,
                                    margin: '20px auto 0',
                                }}
                            />
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>

            <Container maxWidth="xl" sx={{ position: 'relative', zIndex: 1, py: 4 }}>
                <Box
                    sx={{
                        display: 'flex',
                        width: '100%',
                        maxWidth: 1300,
                        margin: '0 auto',
                        gap: { xs: 4, lg: 8 },
                        alignItems: 'center',
                        flexDirection: { xs: 'column', lg: 'row' },
                    }}
                >
                    {/* ── Brand Section ── */}
                    <motion.div
                        initial={{ opacity: 0, x: -60 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
                        style={{ flex: 1.2, minWidth: 0 }}
                    >
                        <Box sx={{ p: { xs: 2, md: 4 } }}>
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                                {/* Logo */}
                                <motion.div
                                    initial={{ scale: 0.8, opacity: 0 }}
                                    animate={{ scale: 1, opacity: 1 }}
                                    transition={{ delay: 0.2, type: 'spring', stiffness: 200 }}
                                >
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2.5 }}>
                                        <PlatformLogo size={86} radius={20} />
                                        <Box>
                                            <Typography
                                                sx={{
                                                    fontFamily: fontDisplay,
                                                    fontWeight: 700,
                                                    fontSize: { xs: '1.8rem', md: '2.4rem' },
                                                    color: palette.ink,
                                                    letterSpacing: '-0.02em',
                                                    lineHeight: 1.1,
                                                }}
                                            >
                                                TELCOFAIR
                                            </Typography>
                                            <Chip
                                                icon={<DiamondIcon sx={{ fontSize: 12, color: `${palette.rust} !important` }} />}
                                                label="ADMIN PORTAL"
                                                sx={{
                                                    mt: 0.5,
                                                    bgcolor: 'color-mix(in srgb,var(--primary,#2E6F8E) 10%,transparent)',
                                                    color: palette.rust,
                                                    fontWeight: 700,
                                                    letterSpacing: '0.08em',
                                                    fontSize: '0.55rem',
                                                    borderRadius: '6px',
                                                    border: `1px solid color-mix(in srgb,var(--primary,#2E6F8E) 20%,transparent)`,
                                                    fontFamily: fontMono,
                                                    height: 24,
                                                }}
                                            />
                                        </Box>
                                    </Box>
                                </motion.div>

                                {/* Title */}
                                <motion.div
                                    initial={{ opacity: 0, y: 20 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ delay: 0.3 }}
                                >
                                    <Typography
                                        sx={{
                                            fontFamily: fontDisplay,
                                            fontWeight: 600,
                                            fontSize: { xs: '1.4rem', md: '1.8rem' },
                                            color: palette.ink,
                                            lineHeight: 1.3,
                                        }}
                                    >
                                        Welcome to the{' '}
                                        <Box component="span" sx={{ color: palette.rust }}>
                                            Admin Portal
                                        </Box>
                                    </Typography>
                                    <Typography
                                        sx={{
                                            color: palette.inkSoft,
                                            fontFamily: fontBody,
                                            fontSize: '0.95rem',
                                            lineHeight: 1.8,
                                            maxWidth: 480,
                                            mt: 1,
                                        }}
                                    >
                                        Exclusive access portal for system administrators.
                                        Manage, monitor, and orchestrate the entire telecom
                                        ecosystem from one powerful command center.
                                    </Typography>
                                </motion.div>

                                {/* Features grid */}
                                <motion.div
                                    initial={{ opacity: 0 }}
                                    animate={{ opacity: 1 }}
                                    transition={{ delay: 0.4 }}
                                >
                                    <Box
                                        sx={{
                                            display: 'grid',
                                            gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
                                            gap: 1.5,
                                        }}
                                    >
                                        {features.map((feature, idx) => (
                                            <motion.div
                                                key={idx}
                                                initial={{ opacity: 0, y: 20 }}
                                                animate={{ opacity: 1, y: 0 }}
                                                transition={{ delay: 0.5 + idx * 0.06 }}
                                                onMouseEnter={() => setHoveredFeature(idx)}
                                                onMouseLeave={() => setHoveredFeature(null)}
                                            >
                                                <Box
                                                    sx={{
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        gap: 1.5,
                                                        p: 1.5,
                                                        borderRadius: '14px',
                                                        background: hoveredFeature === idx
                                                            ? `linear-gradient(135deg, color-mix(in srgb,var(--primary,#2E6F8E) 8%,transparent), transparent)`
                                                            : 'transparent',
                                                        border: `1px solid ${hoveredFeature === idx ? 'color-mix(in srgb,var(--primary,#2E6F8E) 20%,transparent)' : palette.hairlineSoft}`,
                                                        transition: 'all 0.4s cubic-bezier(0.22, 1, 0.36, 1)',
                                                        cursor: 'default',
                                                    }}
                                                >
                                                    <Box
                                                        sx={{
                                                            width: 38,
                                                            height: 38,
                                                            borderRadius: '10px',
                                                            display: 'grid',
                                                            placeItems: 'center',
                                                            background: hoveredFeature === idx
                                                                ? 'color-mix(in srgb,var(--primary,#2E6F8E) 12%,transparent)'
                                                                : 'color-mix(in srgb,var(--primary,#2E6F8E) 5%,transparent)',
                                                            color: hoveredFeature === idx ? palette.rust : palette.inkSoft,
                                                            transition: 'all 0.3s ease',
                                                        }}
                                                    >
                                                        {feature.icon}
                                                    </Box>
                                                    <Box sx={{ flex: 1, minWidth: 0 }}>
                                                        <Typography
                                                            sx={{
                                                                fontFamily: fontBody,
                                                                fontWeight: 600,
                                                                fontSize: '0.8rem',
                                                                color: hoveredFeature === idx ? palette.rust : palette.ink,
                                                                transition: 'color 0.3s ease',
                                                            }}
                                                        >
                                                            {feature.title}
                                                        </Typography>
                                                        <Typography
                                                            sx={{
                                                                fontFamily: fontBody,
                                                                fontSize: '0.7rem',
                                                                color: palette.inkLight,
                                                            }}
                                                        >
                                                            {feature.desc}
                                                        </Typography>
                                                    </Box>
                                                    <motion.div
                                                        animate={{
                                                            opacity: hoveredFeature === idx ? 1 : 0,
                                                            x: hoveredFeature === idx ? 0 : -8,
                                                        }}
                                                        transition={{ duration: 0.3 }}
                                                    >
                                                        <ArrowForwardIcon sx={{ fontSize: 16, color: palette.rust }} />
                                                    </motion.div>
                                                </Box>
                                            </motion.div>
                                        ))}
                                    </Box>
                                </motion.div>

                                {/* Stats */}
                                <motion.div
                                    initial={{ opacity: 0, y: 20 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ delay: 0.6 }}
                                >
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            gap: 4,
                                            pt: 2,
                                            flexWrap: 'wrap',
                                            borderTop: `1px solid ${palette.hairlineSoft}`,
                                        }}
                                    >
                                        {[
                                            { num: '1,200+', label: 'Active Companies', icon: <BusinessIcon  key="config-708-0-2" /> },
                                            { num: '5,000+', label: 'Users Connected', icon: <PeopleIcon  key="config-709-1-2" /> },
                                            { num: '98%', label: 'Satisfaction Rate', icon: <StarsIcon  key="config-710-2-2" /> },
                                        ].map((stat, i) => (
                                            <motion.div
                                                key={i}
                                                initial={{ opacity: 0, y: 10 }}
                                                animate={{ opacity: 1, y: 0 }}
                                                transition={{ delay: 0.7 + i * 0.08 }}
                                                whileHover={{ y: -4 }}
                                                style={{ cursor: 'default' }}
                                            >
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                                    <Box
                                                        sx={{
                                                            width: 32,
                                                            height: 32,
                                                            borderRadius: '8px',
                                                            display: 'grid',
                                                            placeItems: 'center',
                                                            bgcolor: 'color-mix(in srgb,var(--primary,#2E6F8E) 6%,transparent)',
                                                            color: palette.rust,
                                                        }}
                                                    >
                                                        {stat.icon}
                                                    </Box>
                                                    <Box>
                                                        <Typography
                                                            sx={{
                                                                fontFamily: fontDisplay,
                                                                fontWeight: 700,
                                                                fontSize: '1.5rem',
                                                                color: palette.ink,
                                                                lineHeight: 1.2,
                                                            }}
                                                        >
                                                            {stat.num}
                                                        </Typography>
                                                        <Typography
                                                            sx={{
                                                                fontFamily: fontMono,
                                                                fontSize: '0.55rem',
                                                                fontWeight: 500,
                                                                color: palette.inkLight,
                                                                textTransform: 'uppercase',
                                                                letterSpacing: '0.06em',
                                                            }}
                                                        >
                                                            {stat.label}
                                                        </Typography>
                                                    </Box>
                                                </Box>
                                            </motion.div>
                                        ))}
                                    </Box>
                                </motion.div>

                                {/* Status badges */}
                                <motion.div
                                    initial={{ opacity: 0 }}
                                    animate={{ opacity: 1 }}
                                    transition={{ delay: 0.8 }}
                                    sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}
                                >
                                    <Chip
                                        icon={
                                            <motion.div
                                                animate={{ scale: [1, 1.4, 1], opacity: [1, 0.5, 1] }}
                                                transition={{ duration: 1.5, repeat: Infinity }}
                                            >
                                                <LiveIcon sx={{ fontSize: 10, color: `${palette.pine} !important` }} />
                                            </motion.div>
                                        }
                                        label="System Online"
                                        sx={{
                                            bgcolor: 'rgba(30,86,64,0.08)',
                                            color: palette.pine,
                                            fontWeight: 600,
                                            fontSize: '0.65rem',
                                            borderRadius: '20px',
                                            border: `1px solid rgba(30,86,64,0.15)`,
                                            fontFamily: fontMono,
                                        }}
                                    />
                                    <Chip
                                        icon={<FingerprintIcon sx={{ fontSize: 14, color: `${palette.rust} !important` }} />}
                                        label="MFA Enabled"
                                        sx={{
                                            bgcolor: 'color-mix(in srgb,var(--primary,#2E6F8E) 6%,transparent)',
                                            color: palette.rust,
                                            fontWeight: 600,
                                            fontSize: '0.65rem',
                                            borderRadius: '20px',
                                            border: `1px solid color-mix(in srgb,var(--primary,#2E6F8E) 12%,transparent)`,
                                            fontFamily: fontMono,
                                        }}
                                    />
                                    <Chip
                                        icon={<ShieldIcon sx={{ fontSize: 14, color: `${palette.amber} !important` }} />}
                                        label="SOC2 Compliant"
                                        sx={{
                                            bgcolor: 'rgba(176,122,24,0.06)',
                                            color: palette.amber,
                                            fontWeight: 600,
                                            fontSize: '0.65rem',
                                            borderRadius: '20px',
                                            border: `1px solid rgba(176,122,24,0.12)`,
                                            fontFamily: fontMono,
                                        }}
                                    />
                                </motion.div>
                            </Box>
                        </Box>
                    </motion.div>

                    {/* ── Login Form ── */}
                    <motion.div
                        initial={{ opacity: 0, x: 60, rotateY: -5 }}
                        animate={{ opacity: 1, x: 0, rotateY: 0 }}
                        transition={{
                            duration: 0.9,
                            ease: [0.22, 1, 0.36, 1],
                            delay: 0.2,
                            type: 'spring',
                            stiffness: 100,
                            damping: 20,
                        }}
                        style={{
                            flex: '0 0 440px',
                            maxWidth: '440px',
                            width: '100%',
                        }}
                    >
                        <GlassPanel>
                            {/* Header */}
                            <Box sx={{ textAlign: 'center', mb: 3 }}>
                                <motion.div
                                    initial={{ scale: 0, rotate: -180 }}
                                    animate={{ scale: 1, rotate: 0 }}
                                    transition={{ type: 'spring', stiffness: 200, damping: 15, delay: 0.4 }}
                                >
                                    <Box
                                        sx={{
                                            width: 64,
                                            height: 64,
                                            borderRadius: '18px',
                                            background: GRADIENTS.multi,
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            margin: '0 auto 16px',
                                            boxShadow: `0 8px 32px color-mix(in srgb,var(--primary,#2E6F8E) 30%,transparent)`,
                                            position: 'relative',
                                            '&::after': {
                                                content: '""',
                                                position: 'absolute',
                                                inset: -3,
                                                borderRadius: '21px',
                                                background: GRADIENTS.multi,
                                                opacity: 0.3,
                                                filter: 'blur(8px)',
                                                zIndex: -1,
                                            },
                                        }}
                                    >
                                        <PlatformLogo size={76} radius={18} sx={{ boxShadow: 'none', border: 0 }} />
                                    </Box>
                                </motion.div>
                                <motion.div
                                    initial={{ opacity: 0, y: 10 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ delay: 0.5 }}
                                >
                                    <Typography
                                        sx={{
                                            fontFamily: fontDisplay,
                                            fontWeight: 600,
                                            fontSize: '1.4rem',
                                            color: palette.ink,
                                        }}
                                    >
                                        Admin Portal
                                    </Typography>
                                    <Typography
                                        sx={{
                                            fontFamily: fontBody,
                                            fontSize: '0.82rem',
                                            color: palette.inkLight,
                                            mt: 0.5,
                                        }}
                                    >
                                        🔐 Restricted access — authorized personnel only
                                    </Typography>
                                </motion.div>
                            </Box>

                            {/* Error alert */}
                            <AnimatePresence mode="wait">
                                {error && (
                                    <motion.div
                                        initial={{ opacity: 0, y: -10, height: 0 }}
                                        animate={{ opacity: 1, y: 0, height: 'auto' }}
                                        exit={{ opacity: 0, y: -10, height: 0 }}
                                        transition={{ duration: 0.4 }}
                                    >
                                        <Alert
                                            severity="error"
                                            sx={{
                                                borderRadius: '14px',
                                                mb: 2,
                                                background: `linear-gradient(135deg, rgba(201,52,43,0.06), rgba(201,52,43,0.02))`,
                                                border: `1px solid rgba(201,52,43,0.15)`,
                                                '& .MuiAlert-icon': { color: palette.danger },
                                                fontFamily: fontBody,
                                                fontSize: '0.82rem',
                                                backdropFilter: 'blur(10px)',
                                            }}
                                            icon={<SecurityIcon />}
                                        >
                                            {error}
                                        </Alert>
                                    </motion.div>
                                )}
                            </AnimatePresence>

                            {/* Login form */}
                            <form onSubmit={handleLogin}>
                                <GlassInput
                                    label="Admin Email Address"
                                    type="email"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    required
                                    autoComplete="email"
                                    icon={<EmailIcon sx={{ fontSize: 20, color: palette.inkLight }} />}
                                />

                                <GlassInput
                                    label="Password"
                                    type={showPassword ? 'text' : 'password'}
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    required
                                    autoComplete="current-password"
                                    icon={<LockIcon sx={{ fontSize: 20, color: palette.inkLight }} />}
                                    slotProps={{ input: {
                                        endAdornment: (
                                            <InputAdornment position="end">
                                                <IconButton
                                                    onClick={() => setShowPassword(!showPassword)}
                                                    edge="end"
                                                    sx={{
                                                        '&:hover': { bgcolor: 'color-mix(in srgb,var(--primary,#2E6F8E) 6%,transparent)' },
                                                        transition: 'all 0.2s ease',
                                                    }}
                                                >
                                                    <motion.div
                                                        initial={false}
                                                        animate={{ rotateY: showPassword ? 180 : 0 }}
                                                        transition={{ duration: 0.3 }}
                                                    >
                                                        {showPassword ?
                                                            <VisibilityOff sx={{ color: palette.inkLight, fontSize: 20 }} /> :
                                                            <Visibility sx={{ color: palette.inkLight, fontSize: 20 }} />
                                                        }
                                                    </motion.div>
                                                </IconButton>
                                            </InputAdornment>
                                        ),
                                    } }}
                                />

                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 1.5 }}>
                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                checked={rememberMe}
                                                onChange={(e) => setRememberMe(e.target.checked)}
                                                sx={{
                                                    color: palette.inkLight,
                                                    '&.Mui-checked': { color: palette.rust },
                                                    '& .MuiSvgIcon-root': { fontSize: 20 },
                                                    transition: 'all 0.2s ease',
                                                }}
                                            />
                                        }
                                        label={
                                            <Typography
                                                sx={{
                                                    fontFamily: fontBody,
                                                    fontSize: '0.8rem',
                                                    color: palette.inkSoft,
                                                }}
                                            >
                                                Remember Me
                                            </Typography>
                                        }
                                    />
                                    <Link
                                        to="/reset-password"
                                        style={{
                                            color: palette.rust,
                                            textDecoration: 'none',
                                            fontFamily: fontBody,
                                            fontWeight: 600,
                                            fontSize: '0.78rem',
                                            transition: 'all 0.2s ease',
                                            position: 'relative',
                                        }}
                                        onMouseEnter={(e) => {
                                            e.target.style.color = palette.rustDeep;
                                            e.target.style.transform = 'translateX(2px)';
                                        }}
                                        onMouseLeave={(e) => {
                                            e.target.style.color = palette.rust;
                                            e.target.style.transform = 'translateX(0)';
                                        }}
                                    >
                                        Forgot Password?
                                    </Link>
                                </Box>

                                {/* Submit button */}
                                <motion.div
                                    whileHover={{ scale: 1.02 }}
                                    whileTap={{ scale: 0.98 }}
                                    transition={{ type: 'spring', stiffness: 400 }}
                                >
                                    <Button
                                        type="submit"
                                        fullWidth
                                        variant="contained"
                                        disableElevation
                                        disabled={isLoading || loading}
                                        sx={{
                                            mt: 2.5,
                                            py: 1.6,
                                            borderRadius: '16px',
                                            textTransform: 'none',
                                            fontWeight: 700,
                                            fontFamily: fontBody,
                                            fontSize: '0.95rem',
                                            color: '#fff',
                                            background: GRADIENTS.multi,
                                            backgroundSize: '200% 200%',
                                            boxShadow: `inset 0 1px 0 rgba(255,255,255,0.3), 0 12px 32px -12px color-mix(in srgb,var(--primary,#2E6F8E) 80%,transparent)`,
                                            position: 'relative',
                                            overflow: 'hidden',
                                            transition: 'all 0.3s ease',
                                            animation: 'tfGradientMove 4s ease infinite',
                                            '&::before': {
                                                content: '""',
                                                position: 'absolute',
                                                inset: 0,
                                                background: 'linear-gradient(135deg, rgba(255,255,255,0.15), transparent 60%)',
                                                opacity: 0,
                                                transition: 'opacity 0.3s ease',
                                            },
                                            '&:hover': {
                                                transform: 'translateY(-3px)',
                                                boxShadow: `inset 0 1px 0 rgba(255,255,255,0.3), 0 16px 40px -12px color-mix(in srgb,var(--primary,#2E6F8E) 90%,transparent)`,
                                                '&::before': { opacity: 1 },
                                            },
                                            '&:disabled': {
                                                opacity: 0.6,
                                                transform: 'none',
                                                boxShadow: `0 8px 24px -12px color-mix(in srgb,var(--primary,#2E6F8E) 40%,transparent)`,
                                            },
                                        }}
                                    >
                                        {isLoading || loading ? (
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                                <CircularProgress size={20} color="inherit" />
                                                <span>Authenticating...</span>
                                            </Box>
                                        ) : (
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                                <AutoAwesomeIcon sx={{ fontSize: 18 }} />
                                                <span>Sign In as Admin</span>
                                                <ArrowForwardIcon sx={{ fontSize: 18 }} />
                                            </Box>
                                        )}
                                    </Button>
                                </motion.div>
                            </form>

                            <Divider sx={{ my: 3, borderColor: palette.hairlineSoft }}>
                                <Chip
                                    icon={<ShieldIcon sx={{ fontSize: 14, color: `${palette.inkLight} !important` }} />}
                                    label="Secure Admin Login"
                                    sx={{
                                        fontFamily: fontMono,
                                        color: palette.inkLight,
                                        bgcolor: 'transparent',
                                        fontSize: '0.6rem',
                                        fontWeight: 600,
                                        letterSpacing: '0.04em',
                                    }}
                                />
                            </Divider>

                            {/* Security badges */}
                            <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1, flexWrap: 'wrap' }}>
                                {securityBadges.map((item, i) => (
                                    <motion.div
                                        key={i}
                                        whileHover={{ scale: 1.08, y: -3 }}
                                        transition={{ type: 'spring', stiffness: 400 }}
                                    >
                                        <Chip
                                            icon={item.icon}
                                            label={item.label}
                                            size="small"
                                            sx={{
                                                borderRadius: '8px',
                                                background: `linear-gradient(90deg, color-mix(in srgb,var(--primary,#2E6F8E) 6%,transparent), color-mix(in srgb,var(--primary,#2E6F8E) 2%,transparent))`,
                                                fontWeight: 600,
                                                fontFamily: fontMono,
                                                color: item.accent,
                                                border: `1px solid color-mix(in srgb,var(--primary,#2E6F8E) 12%,transparent)`,
                                                fontSize: '0.58rem',
                                                height: 26,
                                                transition: 'all 0.2s ease',
                                                '&:hover': {
                                                    borderColor: 'color-mix(in srgb,var(--primary,#2E6F8E) 30%,transparent)',
                                                    background: `linear-gradient(90deg, color-mix(in srgb,var(--primary,#2E6F8E) 12%,transparent), color-mix(in srgb,var(--primary,#2E6F8E) 4%,transparent))`,
                                                },
                                            }}
                                        />
                                    </motion.div>
                                ))}
                            </Box>

                            <Typography
                                sx={{
                                    color: palette.inkLight,
                                    textAlign: 'center',
                                    display: 'block',
                                    mt: 2.5,
                                    fontFamily: fontMono,
                                    fontSize: '0.6rem',
                                    opacity: 0.6,
                                    letterSpacing: '0.04em',
                                }}
                            >
                                🔐 Restricted to Admin access only • All access is logged
                            </Typography>
                        </GlassPanel>
                    </motion.div>
                </Box>
            </Container>

            <style>{`
                @keyframes tfPulse {
                    0%, 100% { opacity: 1; transform: scale(1); }
                    50%      { opacity: .45; transform: scale(.82); }
                }
                @keyframes tfGradientMove {
                    0%   { background-position: 0% 50%; }
                    50%  { background-position: 100% 50%; }
                    100% { background-position: 0% 50%; }
                }
                @keyframes tfGrain {
                    0%   { transform: translate(0, 0); }
                    25%  { transform: translate(-2%, 1%); }
                    50%  { transform: translate(1%, -2%); }
                    75%  { transform: translate(-1%, -1%); }
                    100% { transform: translate(0, 0); }
                }
                * { scrollbar-width: thin; scrollbar-color: rgba(22,18,16,0.16) transparent; }
                *::-webkit-scrollbar { width: 6px; height: 6px; }
                *::-webkit-scrollbar-track { background: transparent; }
                *::-webkit-scrollbar-thumb { background: rgba(22,18,16,0.14); border-radius: 999px; }
                *::-webkit-scrollbar-thumb:hover { background: rgba(22,18,16,0.26); }
                @media (prefers-reduced-motion: reduce) {
                    *, *::before, *::after {
                        animation-duration: .001ms !important;
                        animation-iteration-count: 1 !important;
                        transition-duration: .001ms !important;
                    }
                }
            `}</style>
        </Box>
    );
};

export default Login;